import React, { useMemo } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from './Header';
import Footer from './Footer';
import {
  PILLAR_PATH_TO_SLUG,
  getLandingContent,
  getCanonicalPathForContent,
  getRelatedServiceLandingLinks,
  buildServiceSchema,
  buildFaqSchema,
  buildBreadcrumbSchema,
  buildLocalBusinessJsonLd
} from '../data/serviceLandingContent';
import './ValetParkingPage.css';
import {
  SITE_ORIGIN,
  OG_SHARE_IMAGE_URL,
  OG_SHARE_IMAGE_WIDTH,
  OG_SHARE_IMAGE_HEIGHT,
  SITE_BRAND_NAME,
  SITE_LOGO_ALT
} from '../constants/siteBranding';
import { renderRichParagraph } from '../utils/renderRichParagraph';

const WHATSAPP_HREF = 'https://wa.me/919265472938';

function ServiceLandingPage() {
  const { serviceSlug } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const slug = serviceSlug || PILLAR_PATH_TO_SLUG[pathname];
  const content = getLandingContent(slug);

  const relatedServiceLinks = useMemo(() => {
    const c = getLandingContent(slug);
    return c ? getRelatedServiceLandingLinks(c.slug, 14) : [];
  }, [slug]);

  if (!content) {
    navigate('/services', { replace: true });
    return null;
  }

  const canonicalPath = getCanonicalPathForContent(content);
  const canonical = `${SITE_ORIGIN}${canonicalPath}`;
  const faqSchemaJson = buildFaqSchema(content.faqs);
  const serviceSchemaJson = buildServiceSchema(content.h1, content.metaDescription);
  const breadcrumbSchemaJson = buildBreadcrumbSchema(content.breadcrumbName, canonical);
  const localBusinessJson = buildLocalBusinessJsonLd(content.name, content.metaDescription);

  return (
    <div className="valet-parking-page">
      <Helmet>
        <title>{content.metaTitle}</title>
        <meta name="description" content={content.metaDescription} />
        {(content.focusKeyword || content.metaKeywords) && (
          <meta
            name="keywords"
            content={
              content.focusKeyword
                ? [content.focusKeyword, content.metaKeywords].filter(Boolean).join(', ')
                : content.metaKeywords
            }
          />
        )}
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:title" content={content.metaTitle} />
        <meta property="og:description" content={content.metaDescription} />
        <meta property="og:image" content={OG_SHARE_IMAGE_URL} />
        <meta property="og:image:width" content={String(OG_SHARE_IMAGE_WIDTH)} />
        <meta property="og:image:height" content={String(OG_SHARE_IMAGE_HEIGHT)} />
        <meta
          property="og:image:alt"
          content={`${SITE_BRAND_NAME} — ${content.name} in Ahmedabad — ${SITE_LOGO_ALT}`}
        />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:site_name" content={SITE_BRAND_NAME} />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={content.metaTitle} />
        <meta name="twitter:description" content={content.metaDescription} />
        <meta name="twitter:image" content={OG_SHARE_IMAGE_URL} />
        <meta
          name="twitter:image:alt"
          content={`${SITE_BRAND_NAME} — ${content.name} in Ahmedabad`}
        />
        <script type="application/ld+json">{JSON.stringify(faqSchemaJson)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchemaJson)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchemaJson)}</script>
        <script type="application/ld+json">{JSON.stringify(localBusinessJson)}</script>
      </Helmet>

      <Header />

      <main className="valet-parking-main">
        <article className="valet-parking-article">
          <header className="valet-parking-hero">
            <div className="container">
              <nav aria-label="Breadcrumb" className="valet-breadcrumb">
                <Link to="/">Home</Link>
                <span className="breadcrumb-sep">/</span>
                <Link to="/services">Services</Link>
                <span className="breadcrumb-sep">/</span>
                <span>{content.breadcrumbName}</span>
              </nav>
              <h1 className="valet-parking-h1">{content.h1}</h1>
              <p className="valet-parking-intro">{renderRichParagraph(content.intro, 'intro')}</p>
              <div className="valet-hero-cta">
                <a href="tel:+919265472938" className="valet-cta-btn primary">Call +91 9265472938</a>
                <a href={WHATSAPP_HREF} className="valet-cta-btn secondary" target="_blank" rel="noopener noreferrer">
                  WhatsApp
                </a>
                <Link to="/inquiry" className="valet-cta-btn secondary">Get Free Quote</Link>
              </div>
            </div>
          </header>

          <div className="valet-parking-body container">
            {content.sections.map((section) => (
              <section
                key={section.id}
                className="valet-section"
                aria-labelledby={section.id}
              >
                <h2 id={section.id}>{section.heading}</h2>
                {section.paragraphs.map((p, i) => (
                  <p key={i}>{renderRichParagraph(p, `sec-${section.id}-p${i}`)}</p>
                ))}
                {section.listItems && section.listItems.length > 0 && (
                  <ul>
                    {section.listItems.map((item, i) => (
                      <li key={i}>{renderRichParagraph(item, `sec-${section.id}-li${i}`)}</li>
                    ))}
                  </ul>
                )}
              </section>
            ))}

            {relatedServiceLinks.length > 0 && (
              <section className="valet-section valet-related-links" aria-labelledby="related-services-heading">
                <h2 id="related-services-heading">More services in Ahmedabad</h2>
                <p>
                  Explore other services we cover in Ahmedabad and Gujarat. See the{' '}
                  <Link to="/services">full service catalogue</Link> for every option.
                </p>
                <ul className="valet-related-links-list">
                  {relatedServiceLinks.map((item) => (
                    <li key={item.path}>
                      <Link to={item.path}>{item.label}</Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="valet-section valet-faq" aria-labelledby="faq-heading">
              <h2 id="faq-heading">{content.faqHeading || 'FAQs'}</h2>
              {content.faqs.map((faq, i) => (
                <div key={i} className="faq-item">
                  <h3>{faq.question}</h3>
                  <p>{renderRichParagraph(faq.answer, `faq-a-${i}`)}</p>
                </div>
              ))}
            </section>

            <section className="valet-cta-section">
              <div className="container">
                <h2>Get a Quote for {content.name} in Ahmedabad</h2>
                <p>Contact us for rates and availability. We serve Ahmedabad and Gujarat.</p>
                <div className="valet-hero-cta">
                  <a href="tel:+919265472938" className="valet-cta-btn primary">Call +91 9265472938</a>
                  <a href={WHATSAPP_HREF} className="valet-cta-btn secondary" target="_blank" rel="noopener noreferrer">
                    WhatsApp
                  </a>
                  <Link to="/inquiry" className="valet-cta-btn secondary">Get Free Quote</Link>
                </div>
                <p className="valet-more-services">
                  <Link to="/services">View all our services</Link> – House keeping, cleaning, manpower, pest control, valet parking, and more.
                </p>
              </div>
            </section>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}

export default ServiceLandingPage;
